import React, { useMemo, useState } from 'react';
import { createPortal } from 'react-dom';
import { motion, AnimatePresence } from 'framer-motion';
import axios from 'axios';
import { QRCodeSVG } from 'qrcode.react';
import { X, Zap, Sparkles, Check, Loader2, Smartphone, QrCode, BadgeCheck } from 'lucide-react';
import { useCredits } from '../hooks/useCredits';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// Merchant UPI config — set REACT_APP_UPI_VPA / REACT_APP_UPI_PAYEE to go live.
// The placeholder VPA below is a DEMO handle; payments against it are MOCKED.
const UPI_VPA = process.env.REACT_APP_UPI_VPA || 'studyfolio@upi';
const UPI_PAYEE = process.env.REACT_APP_UPI_PAYEE || 'Studyfolio';
const IS_DEMO_VPA = !process.env.REACT_APP_UPI_VPA;

const TIERS = [
    { price: 29, credits: 15, badge: null },
    { price: 49, credits: 35, badge: 'POPULAR' },
    { price: 99, credits: 85, badge: 'BEST VALUE' },
    { price: 199, credits: 200, badge: 'BONUS' },
];

const UPI_APPS = [
    { id: 'gpay', label: 'Google Pay', scheme: 'tez://upi/pay' },
    { id: 'phonepe', label: 'PhonePe', scheme: 'phonepe://pay' },
    { id: 'paytm', label: 'Paytm', scheme: 'paytmmp://pay' },
    { id: 'any', label: 'Any UPI App', scheme: 'upi://pay' },
];

const fmt = (n) => '₹' + Number(n).toLocaleString('en-IN');

/**
 * MOCKED payment verification — simulates a gateway webhook round-trip.
 *
 * TO GO LIVE: replace this with a Supabase Realtime subscription on a
 * `payment_orders` table written by your UPI gateway webhook (Razorpay/
 * Cashfree/PayU), e.g.:
 *
 *   const channel = supabase
 *     .channel(`order-${orderId}`)
 *     .on('postgres_changes',
 *         { event: 'UPDATE', schema: 'public', table: 'payment_orders',
 *           filter: `order_id=eq.${orderId}` },
 *         (payload) => { if (payload.new.status === 'paid') resolve(true); })
 *     .subscribe();
 *
 * The UI below already waits asynchronously — only this resolver changes.
 */
function verifyPayment(orderId) {
    return new Promise((resolve) => setTimeout(() => resolve({ ok: true, orderId }), 2200));
}

export default function QuickTopUp({ open, onClose }) {
    const { credits, grant } = useCredits();
    const [step, setStep] = useState('tiers'); // tiers | upi | verifying | done
    const [selected, setSelected] = useState(null); // {price, credits}
    const [customAmount, setCustomAmount] = useState('');
    const [orderId] = useState(() => `sf_${Date.now().toString(36)}`);

    const active = useMemo(() => {
        if (selected) return selected;
        const amt = parseInt(customAmount, 10);
        if (Number.isFinite(amt) && amt >= 10) return { price: amt, credits: amt, custom: true };
        return null;
    }, [selected, customAmount]);

    const upiString = useMemo(() => {
        if (!active) return '';
        const params = new URLSearchParams({
            pa: UPI_VPA, pn: UPI_PAYEE, am: String(active.price), cu: 'INR',
            tn: `Studyfolio ${active.credits} credits ${orderId}`,
        });
        return `upi://pay?${params.toString()}`;
    }, [active, orderId]);

    function reset() { setStep('tiers'); setSelected(null); setCustomAmount(''); }
    function close() { reset(); onClose?.(); }

    async function handleVerify() {
        if (!active) return;
        setStep('verifying');
        const res = await verifyPayment(orderId);
        if (res.ok) {
            grant(active.credits);
            try { await axios.post(`${API}/credits/grant`, { amount: active.credits, reason: `UPI top-up ${orderId}` }); } catch {}
            setStep('done');
        } else {
            setStep('upi');
        }
    }

    if (!open) return null;
    return createPortal(
        <motion.div
            initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}
            className="fixed inset-0 z-[350] flex items-center justify-center p-4"
            style={{ background: 'rgba(11,15,7,0.72)', backdropFilter: 'blur(6px)' }}
            onClick={close}
            data-testid="quick-topup-modal"
        >
            <motion.div
                initial={{ y: 24, opacity: 0, scale: 0.97 }} animate={{ y: 0, opacity: 1, scale: 1 }}
                onClick={(e) => e.stopPropagation()}
                className="w-full max-w-lg rounded-lg overflow-hidden border border-[#3e4438] bg-[#1a1e17]/95 backdrop-blur-xl"
                style={{ boxShadow: '0 40px 80px -25px rgba(0,0,0,0.75)' }}
            >
                {/* Header */}
                <div className="flex items-center justify-between px-6 py-4 border-b border-[#3e4438]">
                    <div>
                        <div className="display text-[#f0f4ee] text-xl flex items-center gap-2">
                            <Zap size={16} className="text-[#D4B3FF]" fill="currentColor" /> Quick Top-Up
                        </div>
                        <div className="mono text-[9px] text-[#8a957f] mt-0.5">
                            UPI · INSTANT · BALANCE NOW: <span className="text-[#D4B3FF]">{credits} CREDITS</span>
                        </div>
                    </div>
                    <button onClick={close} data-testid="quick-topup-close"
                        className="p-1.5 rounded-sm text-[#c2ccbe] hover:text-[#f0f4ee] hover:bg-white/10 transition-colors" aria-label="Close">
                        <X size={16} />
                    </button>
                </div>

                <div className="px-6 py-5 max-h-[78vh] overflow-y-auto book-scroll">
                    {step === 'tiers' && (
                        <>
                            <div className="grid grid-cols-2 gap-3">
                                {TIERS.map((t) => (
                                    <button key={t.price}
                                        onClick={() => { setSelected(t); setCustomAmount(''); }}
                                        data-testid={`topup-tier-${t.price}`}
                                        className={`relative rounded-md border p-4 text-left transition-all duration-200 hover:-translate-y-0.5 ${
                                            selected?.price === t.price
                                                ? 'border-[#B79CFF] bg-[#B79CFF]/15 ring-1 ring-[#B79CFF]/60'
                                                : 'border-[#3e4438] bg-white/5 hover:border-[#B79CFF]/50'
                                        }`}>
                                        {t.badge && (
                                            <span className="absolute -top-2 left-3 mono text-[8px] tracking-[0.15em] bg-[#B79CFF] text-[#1a1e17] px-1.5 py-0.5 rounded-sm font-semibold">
                                                {t.badge}
                                            </span>
                                        )}
                                        <div className="display text-[#f0f4ee] text-2xl tabular-nums">{fmt(t.price)}</div>
                                        <div className="mono text-[10px] text-[#D4B3FF] mt-1">{t.credits} credits</div>
                                        <div className="mono text-[9px] text-[#8a957f] mt-0.5">{(t.price / t.credits).toFixed(2)} ₹/credit</div>
                                    </button>
                                ))}
                            </div>

                            <div className="mt-4">
                                <div className="mono text-[9px] tracking-[0.2em] text-[#8a957f] mb-1.5">CUSTOM AMOUNT (MIN ₹10 · ₹1 = 1 CREDIT)</div>
                                <div className="flex items-center gap-2">
                                    <span className="text-[#c2ccbe] text-sm">₹</span>
                                    <input
                                        type="number" min="10" max="5000"
                                        value={customAmount}
                                        onChange={(e) => { setCustomAmount(e.target.value); setSelected(null); }}
                                        placeholder="e.g. 75"
                                        data-testid="topup-custom-input"
                                        className="flex-1 bg-white/5 border border-[#3e4438] rounded-sm px-3 py-2 text-[13px] text-[#f0f4ee] placeholder:text-[#8a957f] outline-none focus:border-[#B79CFF]/60"
                                    />
                                </div>
                            </div>

                            <button
                                disabled={!active}
                                onClick={() => setStep('upi')}
                                data-testid="topup-continue"
                                className="w-full mt-5 py-3 rounded-sm mono text-[11px] font-semibold text-[#1a1e17] bg-[#B79CFF] hover:bg-[#CBB4FF] transition-colors disabled:opacity-40">
                                {active ? `Continue · ${fmt(active.price)} for ${active.credits} credits →` : 'Pick a pack or enter an amount'}
                            </button>
                        </>
                    )}

                    {(step === 'upi' || step === 'verifying') && active && (
                        <div className="text-center">
                            <div className="mono text-[10px] tracking-[0.2em] text-[#8a957f]">
                                ORDER <span className="text-[#c2ccbe]">{orderId}</span>
                            </div>
                            <div className="display text-[#f0f4ee] text-3xl mt-1 tabular-nums" data-testid="upi-amount">
                                {fmt(active.price)} <span className="text-sm mono text-[#D4B3FF]">→ {active.credits} credits</span>
                            </div>

                            {/* QR */}
                            <div className="mx-auto mt-4 w-fit p-3 rounded-md bg-white" data-testid="upi-qr">
                                <QRCodeSVG value={upiString} size={168} level="M" />
                            </div>
                            <div className="mono text-[9px] text-[#8a957f] mt-2 flex items-center justify-center gap-1">
                                <QrCode size={10} /> Scan with any UPI app · {UPI_VPA} {IS_DEMO_VPA && <span className="text-amber-400">(DEMO VPA — MOCKED)</span>}
                            </div>

                            {/* App deep links */}
                            <div className="grid grid-cols-2 gap-2 mt-4">
                                {UPI_APPS.map((a) => (
                                    <a key={a.id}
                                        href={`${a.scheme}?${upiString.split('?')[1]}`}
                                        data-testid={`upi-app-${a.id}`}
                                        className="flex items-center justify-center gap-2 border border-[#3e4438] bg-white/5 hover:bg-white/10 hover:border-[#B79CFF]/50 rounded-sm py-2.5 mono text-[10px] text-[#f0f4ee] transition-colors">
                                        <Smartphone size={12} className="text-[#D4B3FF]" /> {a.label}
                                    </a>
                                ))}
                            </div>

                            <button
                                onClick={handleVerify}
                                disabled={step === 'verifying'}
                                data-testid="topup-verify"
                                className="w-full mt-5 py-3 rounded-sm mono text-[11px] font-semibold text-[#1a1e17] bg-[#B79CFF] hover:bg-[#CBB4FF] transition-colors disabled:opacity-60 flex items-center justify-center gap-2">
                                {step === 'verifying'
                                    ? <><Loader2 size={13} className="animate-spin" /> Verifying payment…</>
                                    : "I've Paid — Verify & Add Credits"}
                            </button>
                            <button onClick={() => setStep('tiers')} data-testid="topup-back"
                                className="mt-2 mono text-[10px] text-[#8a957f] hover:text-[#c2ccbe]">
                                ← back to packs
                            </button>
                        </div>
                    )}

                    {step === 'done' && active && (
                        <div className="text-center py-6" data-testid="topup-success">
                            <div className="h-14 w-14 mx-auto rounded-full bg-[#B79CFF]/20 border border-[#B79CFF]/60 flex items-center justify-center">
                                <BadgeCheck size={26} className="text-[#D4B3FF]" />
                            </div>
                            <div className="display text-[#f0f4ee] text-2xl mt-3">Payment received.</div>
                            <div className="text-[#c2ccbe] text-[13px] mt-1">
                                +{active.credits} credits added — new balance <span className="text-[#D4B3FF] font-semibold" data-testid="topup-new-balance">{credits} credits</span>.
                            </div>
                            <div className="mono text-[9px] text-[#8a957f] mt-2">Balance updated in real time — no refresh needed.</div>
                            <button onClick={close} data-testid="topup-done"
                                className="mt-5 px-6 py-2.5 rounded-sm mono text-[11px] font-semibold text-[#1a1e17] bg-[#B79CFF] hover:bg-[#CBB4FF] transition-colors">
                                Back to studying →
                            </button>
                        </div>
                    )}
                </div>
            </motion.div>
        </motion.div>,
        document.body,
    );
}
